'use client';

import { useMemo } from 'react';

import { RadioTile, RadioTiles, Text, TextLevels, TextMarkup, TypographyBold } from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { CategoryBox } from '@product-detail/components';

interface WithOrWithoutPlanProps {
    type: WallType;
    plans: MobilePlanType[];
    currentPlan: MobilePlanType | null;
    withoutPlanPrice: number;
    setCurrentPlan: (hasPlan: boolean) => void;
}

export function WithOrWithoutPlan({
    type,
    plans,
    currentPlan,
    withoutPlanPrice,
    setCurrentPlan,
}: Readonly<WithOrWithoutPlanProps>) {
    const handleWithPlan = () => {
        setCurrentPlan(true);
    };

    const handleWithoutPlan = () => {
        setCurrentPlan(false);
    };

    const minimumPrice = useMemo(() => {
        return getMinimumPhonePrice(plans);
    }, [plans]);

    const productType = useMemo(() => {
        switch (type) {
            case WallType.PHONE:
                return 'téléphone';
            case WallType.HOTSPOT:
                return 'hotspot';
            default:
                return 'produit';
        }
    }, [type]);

    return (
        <CategoryBox title='Avec ou sans forfait&nbsp;?' formId='withPlans'>
            <RadioTiles>
                <RadioTile
                    id='withPlan'
                    horizontal
                    label='Avec forfait'
                    description={
                        minimumPrice < Infinity ? (
                            <>
                                <Text level={TextLevels.TWO} markup={TextMarkup.SPAN}>
                                    Prix du {productType}&nbsp;:{' '}
                                </Text>
                                <Text
                                    level={TextLevels.TWO}
                                    markup={TextMarkup.SPAN}
                                    typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                >
                                    à partir de <InlinePrice level={TextLevels.TWO} amount={minimumPrice} />
                                </Text>
                            </>
                        ) : undefined
                    }
                    name='plan'
                    onChange={handleWithPlan}
                    checked={currentPlan !== null}
                />
                <RadioTile
                    id='noPlan'
                    horizontal
                    label='Sans forfait'
                    description={
                        <>
                            <Text level={TextLevels.TWO} markup={TextMarkup.SPAN}>
                                Prix du {productType}&nbsp;:{' '}
                            </Text>

                            <InlinePrice level={TextLevels.TWO} amount={withoutPlanPrice} />
                        </>
                    }
                    name='plan'
                    onChange={handleWithoutPlan}
                    checked={currentPlan === null}
                />
            </RadioTiles>
        </CategoryBox>
    );
}

const getMinimumPhonePrice = (plans: MobilePlanType[]) => {
    return plans
        .map((p) => p.simulatedCart?.quotes[0]?.products[0]?.details?.price.final || Infinity)
        .reduce((min, price) => Math.min(min, price), Infinity);
};
